import { prewarmHlsSession } from "@ui/molecules/VideoPreview/useHlsSource";
import {
	createContext,
	createEffect,
	type ParentComponent,
	Show,
	useContext,
} from "solid-js";
import type { VideoEvidence } from "../ChatEventTypes";
import "./ChatVideoEvidence.css";
import { CoverButton } from "@ui/CoverButton";
import { usePendingImage } from "@ui/hooks/usePendingImage";
import {
	VideoCarousel,
	type VideoCarouselSlideContext,
} from "@ui/molecules/VideoCarousel/VideoCarousel";
import { VideoPreview } from "@ui/molecules/VideoPreview/VideoPreview";
import { useClipBoardMembership } from "~/modules/shared/boards/boardsContext";
import { videoEvidenceHlsSource } from "../utils/videoEvidenceHlsSource";
import { videoMentionFromClip } from "../utils/videoEvidenceMentions";
import { AddToBoardButton } from "./AddToBoardButton";
import {
	addVideoMentionToClipboard,
	isVideoMentionInClipboard,
} from "./clipboard/clipboardStore";
import { useOffersClipboard } from "./clipboard/offersClipboard";

const OffersAddToBoardContext = createContext<{ enabled: boolean }>({
	enabled: true,
});

/**
 * Sets whether the clips inside it offer to be added to a board. On unless
 * turned off, e.g. for a signed-out reader, who has no boards to add to.
 */
export const ChatVideoEvidenceAddToBoardProvider: ParentComponent<{
	enabled: boolean;
}> = (props) => (
	<OffersAddToBoardContext.Provider
		value={{
			get enabled() {
				return props.enabled;
			},
		}}
	>
		{props.children}
	</OffersAddToBoardContext.Provider>
);

type Props = {
	appEmbedded: boolean;
	interactionId: string;
	videoEvidence: VideoEvidence[];
	projectId?: string;
	setIsVideoPanelOpen: (
		isOpen: boolean,
		videoIndex: number,
		videoEvidence: VideoEvidence[],
	) => void;
};

type SlideProps = {
	appEmbedded: boolean;
	clipBoard: ReturnType<typeof useClipBoardMembership>;
	videoEvidence: VideoEvidence;
	offersClipboard: boolean;
	offersAddToBoard: boolean;
	ctx: VideoCarouselSlideContext;
};

const ChatVideoEvidenceSlide = (props: SlideProps) => {
	const existingBoard = () =>
		props.clipBoard.getClipBoardEntry(props.videoEvidence);
	const mention = () =>
		videoMentionFromClip(props.videoEvidence, props.ctx.index + 1);
	const inClipboard = () => isVideoMentionInClipboard(mention());
	const title = () => props.videoEvidence.title?.trim() || undefined;
	const thumbnail = usePendingImage(() => props.videoEvidence.thumbnail_url);
	const playLabel = () =>
		title()
			? `Play clip ${props.ctx.index + 1}: ${title()}`
			: `Play clip ${props.ctx.index + 1}`;

	return (
		<>
			<Show when={props.appEmbedded}>
				<Show
					when={thumbnail.src()}
					fallback={
						<div
							class="video-thumbnail-placeholder"
							classList={{
								"video-thumbnail-placeholder--portrait": props.ctx.isPortrait,
								"video-thumbnail-placeholder--landscape": !props.ctx.isPortrait,
							}}
						/>
					}
				>
					<img
						class="video-thumbnail"
						classList={{
							"video-thumbnail--portrait": props.ctx.isPortrait,
							"video-thumbnail--landscape": !props.ctx.isPortrait,
						}}
						alt={title() ?? props.videoEvidence.description}
						src={thumbnail.src()}
					/>
				</Show>
				{/* A bare thumbnail has nothing to hang the slide's action on. */}
				<CoverButton label={playLabel()} onActivate={props.ctx.activate} />
			</Show>
			<Show when={!props.appEmbedded}>
				<VideoPreview
					portrait={props.ctx.isPortrait}
					onActivate={props.ctx.activate}
					activateLabel={playLabel()}
					forceShowChildren={true}
					playTrigger="hover"
					thumbnailUrl={props.videoEvidence.thumbnail_url}
					videoUrl={props.videoEvidence.video_url}
					hlsSource={videoEvidenceHlsSource(props.videoEvidence)}
					startSeconds={Math.round(props.videoEvidence.start_time)}
					endSeconds={Math.round(props.videoEvidence.end_time)}
					showDuration={true}
					durationAction={
						props.offersClipboard
							? {
									symbol: inClipboard() ? "check" : "add",
									label: `Add video ${mention().index}`,
									done: inClipboard(),
									onActivate: () => addVideoMentionToClipboard(mention()),
								}
							: undefined
					}
					forceMuteAudio={true}
				>
					{/* Left out rather than hidden: VideoPreview draws its hover-actions box whenever it has children. */}
					{props.offersAddToBoard ? (
						<AddToBoardButton
							existingBoard={existingBoard}
							membershipLoading={props.clipBoard.isLoading}
							videoEvidence={props.videoEvidence}
							addClip={props.clipBoard.addClip}
						/>
					) : undefined}
				</VideoPreview>
			</Show>
		</>
	);
};

export const ChatVideoEvidence = (props: Props) => {
	const offersClipboard = useOffersClipboard();
	const addToBoard = useContext(OffersAddToBoardContext);

	// No clips means no membership lookup, which a signed-out reader can't make.
	const clipBoard = useClipBoardMembership(
		props.projectId,
		addToBoard.enabled
			? props.videoEvidence.map((video) => ({
					video_id: video.video_id,
					start: video.start_time,
					end: video.end_time,
					video_duration: video.video_duration,
				}))
			: [],
		{
			appEmbedded: props.appEmbedded,
		},
	);

	// Hover previews: authorize every clip up front so hovering doesn't wait
	// on a session mint. App-embedded chats render bare thumbnails instead.
	createEffect(() => {
		if (props.appEmbedded) {
			return;
		}
		prewarmHlsSession(
			props.videoEvidence.flatMap(
				(evidence) => videoEvidenceHlsSource(evidence)?.videoId ?? [],
			),
		);
	});

	const onClipClick = (index: number) => {
		if (!props.appEmbedded) {
			props.setIsVideoPanelOpen(true, index, props.videoEvidence);
			return;
		}
		const detail = {
			interaction_id: props.interactionId,
			videos: props.videoEvidence,
			video_index: index,
		};
		document.dispatchEvent(new CustomEvent("videocarouselplay", { detail }));
	};

	return (
		<VideoCarousel
			items={props.videoEvidence}
			scrollMode={props.appEmbedded ? "mousewheel" : "buttons"}
			onSlideClick={(index) => {
				onClipClick(index);
			}}
			renderSlide={(videoEvidence, ctx) => (
				<ChatVideoEvidenceSlide
					appEmbedded={props.appEmbedded}
					clipBoard={clipBoard}
					videoEvidence={videoEvidence}
					offersClipboard={offersClipboard()}
					offersAddToBoard={addToBoard.enabled}
					ctx={ctx}
				/>
			)}
		/>
	);
};
